import React, { useCallback, useRef } from 'react';
import { useMutation } from '@apollo/client';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { EquipmentType } from '@trnsact/trnsact-shared-types';
import { useEquipmentActions } from 'modules/equipment';
import { DealBuilderFormValues } from '../../../types';
import { EMPTY_TRADE_IN_ROW } from '../../../constants';
import { useDealBuilderContext } from '../../../context/DealBuilderContext';
import { dealBuilderApi, UpdateDealBuilderQuoteResponse, UpdateDealBuilderQuoteVariables } from '../../../api';
import { TradeInEmptyState } from './TradeInEmptyState';
import { TradeInListing } from './TradeInListing';
import { quoteAPI } from '../../../../quote/api';

export const TradeInSection = () => {
  const { control, getValues, setValue } = useFormContext<DealBuilderFormValues>();
  const {
    activeEntity,
    setActiveEntity,
    sourceData: { vo },
  } = useDealBuilderContext();
  const vendorOpportunityId = vo?.vendorOpportunityId ?? '';
  const dynamicsVOId = vo?.dynamicsVendorOpportunityId ?? '';

  const { fields, append, remove } = useFieldArray({ control, name: 'pricing.tradeIn' });
  const { onUpsertEquipmentRecord, onDeleteEquipment } = useEquipmentActions();

  const [updateDealBuilderQuoteMutation] = useMutation<UpdateDealBuilderQuoteResponse, UpdateDealBuilderQuoteVariables>(
    dealBuilderApi.updateDealBuilderQuote,
    {
      refetchQueries: [quoteAPI.getQuoteCalcById],
      awaitRefetchQueries: true,
    }
  );

  const rowTimersRef = useRef<Record<number, ReturnType<typeof setTimeout>>>({});

  const persistQuoteTradeIns = useCallback(async () => {
    const quoteId = activeEntity?.quote?.quoteId;
    if (!quoteId || !vendorOpportunityId) return;

    const rows = getValues('pricing.tradeIn').filter(row => row.equipmentId);
    const result = await updateDealBuilderQuoteMutation({
      variables: {
        quoteId,
        input: {
          vendorOpportunityId,
          equipmentTradeIns: rows.map(row => ({
            equipmentId: row.equipmentId,
            vin: row.serialNumberVin || undefined,
            tradeInDetails: {
              tradeValue: row.tradeInValue ?? 0,
              tradeAmountOwed: row.amountOwed ?? 0,
              tradeOtherCharges: row.otherCharges ?? 0,
            },
          })),
        },
      },
    });

    const updatedQuote = result.data?.dealBuilder?.updateDealBuilderQuote?.entity;
    if (updatedQuote && activeEntity) {
      setActiveEntity({ ...activeEntity, quote: updatedQuote });
    }
  }, [activeEntity, getValues, updateDealBuilderQuoteMutation, setActiveEntity, vendorOpportunityId]);

  const persistRow = useCallback(
    (rowIndex: number) => {
      clearTimeout(rowTimersRef.current[rowIndex]);
      rowTimersRef.current[rowIndex] = setTimeout(async () => {
        const row = getValues(`pricing.tradeIn.${rowIndex}`);

        const equipmentId = await onUpsertEquipmentRecord({
          equipmentId: row.equipmentId ?? undefined,
          type: EquipmentType.TradeIn,
          quantity: 1,
          make: row.make,
          model: row.model,
          year: row.year,
          newUsed: row.newUsed ?? '',
          serialNumberVin: row.serialNumberVin ?? '',
          mileage: row.mileage,
          tradeInValue: row.tradeInValue,
          amountOwedOnTradeIn: row.amountOwed,
        });

        if (!row.equipmentId) setValue(`pricing.tradeIn.${rowIndex}.equipmentId`, equipmentId);
        await persistQuoteTradeIns();
      }, 1500);
    },
    [getValues, setValue, onUpsertEquipmentRecord, persistQuoteTradeIns, vendorOpportunityId, dynamicsVOId]
  );

  const handleAddRow = () => append(EMPTY_TRADE_IN_ROW);

  const handleRemoveRow = (rowIndex: number) => {
    const row = getValues(`pricing.tradeIn.${rowIndex}`);
    if (row.equipmentId) {
      // onDeleteEquipment shows its own confirmation dialog before deleting
      onDeleteEquipment(
        { equipmentId: row.equipmentId, vendorOpportunityId, dynamicsVendorOpportunityId: dynamicsVOId },
        () => {
          remove(rowIndex);
          persistQuoteTradeIns();
        }
      );
    } else {
      remove(rowIndex);
    }
  };

  if (!fields.length) {
    return <TradeInEmptyState onAdd={handleAddRow} />;
  }

  return (
    <TradeInListing
      control={control}
      fields={fields}
      onFieldBlur={persistRow}
      onRemoveRow={handleRemoveRow}
      onAddRow={handleAddRow}
    />
  );
};
